
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DailyReportCard from './components/DailyReportCard';
import AnalyticsReportCard from './components/AnalyticsReportCard';
import SummaryStatisticsCard from './components/SummaryStatisticsCard';
import DomainSummaryReportCard from './components/DomainSummaryReportCard';
import FilteredApplicantsTable from './components/FilteredApplicantsTable';

import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const defaultDate = new Date();
const defaultRange = {
  start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // last 1 month
  end_date: new Date()
};

const Reports = () => {
  // Daily Report states
  const [dailyReport, setDailyReport] = useState({ data: [], date: null });
  const [selectedMode, setSelectedMode] = useState('today');
  const [selectedDate, setSelectedDate] = useState(defaultDate);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [loading, setLoading] = useState(false);

  // range states for daily report
  const [selectedStartDate, setSelectedStartDate] = useState(defaultRange.start_date);
  const [selectedEndDate, setSelectedEndDate] = useState(defaultRange.end_date);

  // Domains fetched from DB
  const [domains, setDomains] = useState([]);

  // State for Domain Summary
  const [summary, setSummary] = useState([]);
  const [summaryDate, setSummaryDate] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Analytics states
  const [analytics, setAnalytics] = useState([]);
  const [dateRange, setDateRange] = useState(defaultRange);
  const [analyticsDomain, setAnalyticsDomain] = useState('');

  // Fetch domains from backend
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const res = await axios.get(`${base}/api/domains`); // backend route to get all domains
        setDomains(['All', ...res.data]);
      } catch (err) {
        console.error("Error fetching domains:", err);
        setDomains(['All']); // fallback
      }
    };
    fetchDomains();
  }, []);

  // Fetch daily report
  const fetchDailyReport = async (
    mode = selectedMode,
    date = selectedDate,
    domain = selectedDomain,
    startDate = selectedStartDate,
    endDate = selectedEndDate
  ) => {
    setLoading(true);
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      let url = `${base}/api/applicants/reports/daily?mode=${mode}`;

      if (mode === 'date' && date) {
        url += `&date=${date.toISOString().split('T')[0]}`;
      } else if (mode === 'range') {
        // ensure we have Date objects or ISO strings
        const s = startDate instanceof Date ? startDate.toISOString().split('T')[0] : startDate;
        const e = endDate instanceof Date ? endDate.toISOString().split('T')[0] : endDate;
        url += `&start_date=${s}&end_date=${e}`;
      }

      if (domain && domain !== 'All') {
        url += `&domain=${domain}`;
      }

      const res = await axios.get(url, { withCredentials: true });
      const result = res.data;

      if (mode === 'range') {
        // server returns start_date and end_date for range
        const start = result.start_date || (startDate instanceof Date ? startDate.toISOString().split('T')[0] : startDate);
        const end = result.end_date || (endDate instanceof Date ? endDate.toISOString().split('T')[0] : endDate);
        setDailyReport({ data: result.data || [], date: `${start} to ${end}` });
      } else {
        setDailyReport({ data: result.data || [], date: result.date || null });
      }
    } catch (error) {
      setDailyReport({ data: [], date: null });
    } finally {
      setLoading(false);
    }
  };

  // Fetch analytics report
  const fetchAnalytics = async () => {
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      let url = `${base}/api/applicants/reports/analytics?start_date=${dateRange.start_date.toISOString().split('T')[0]}&end_date=${dateRange.end_date.toISOString().split('T')[0]}`;
      if (analyticsDomain && analyticsDomain !== 'All') {
        url += `&domain=${analyticsDomain}`;
      }
      const res = await axios.get(url, { withCredentials: true });
      const result = res.data;
      if (Array.isArray(result)) {
        setAnalytics(result);
      } else if (result.data && Array.isArray(result.data)) {
        setAnalytics(result.data);
      } else {
        setAnalytics([]);
      }
    } catch (error) {
      setAnalytics([]);
    }
  };

  // Effects
  useEffect(() => {
    fetchDailyReport();
    // eslint-disable-next-line
  }, [selectedMode, selectedDate, selectedDomain, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line
  }, [dateRange, analyticsDomain]);

  // CSV Export utility
  const exportToCSV = (data, filename) => {
    if (!data || !data.length) return;
    const csvContent = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).map(v => {
        // simple escaping for commas and quotes
        if (v === null || v === undefined) return '';
        const s = String(v);
        if (s.includes(',') || s.includes('"')) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      }).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-4">Reports & Analytics</h2>
      <DailyReportCard
        dailyReport={dailyReport}
        fetchDailyReport={fetchDailyReport}
        selectedMode={selectedMode}
        setSelectedMode={setSelectedMode}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedDomain={selectedDomain}
        setSelectedDomain={setSelectedDomain}
        exportToCSV={exportToCSV}
        domains={domains}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedStartDate={setSelectedStartDate}
        setSelectedEndDate={setSelectedEndDate}
      />
      <AnalyticsReportCard
        analytics={analytics}
        dateRange={dateRange}
        setDateRange={setDateRange}
        domain={analyticsDomain}
        setDomain={setAnalyticsDomain}
        exportToCSV={exportToCSV}
        domains={domains}
      />
      <SummaryStatisticsCard analytics={analytics} />

      <DomainSummaryReportCard
        startDate={dateRange.start_date.toISOString().split('T')[0]}
        endDate={dateRange.end_date.toISOString().split('T')[0]}
      />

      <FilteredApplicantsTable />
    </div>
  );
};

export default Reports;